import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Divider,
  IconButton,
  Box
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Header from '../Header/Header';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

function MeetingGuidelines({ userId, loggedIn, onLogout }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  return (
    <Box sx={{ backgroundColor: '#121212', color: '#e0e0e0', minHeight: '100vh' }}>
    

      <Container maxWidth="md" sx={{ pt: 12, pb: 6 }}>
        <Typography variant="h3" component="h1" textAlign="center" gutterBottom sx={{ letterSpacing: 4 }}>
          Session Instructions
        </Typography>

        <Typography variant="body1" paragraph sx={{ letterSpacing: 2 }}>
          Whether you are a verified or a general user, your adherence to our session guidelines ensures a positive and professional experience for everyone.
        </Typography>

        <Typography variant="body1" paragraph sx={{ letterSpacing: 2 }}>
          Please take a moment to review these important instructions and behavioral standards before joining any live sessions.
        </Typography>

        <Divider sx={{ my: 4, borderColor: '#333' }} />

        {/* Quick Tips */}
        <Typography variant="h5" gutterBottom sx={{ letterSpacing: 3, textAlign: 'center' }}>
          Pre-Session Checklist
        </Typography>
        <Typography variant="body2" paragraph sx={{ letterSpacing: 2 }}>
          ✅ Ensure your microphone and camera are working properly before joining.
        </Typography>
        <Typography variant="body2" paragraph sx={{ letterSpacing: 2 }}>
          🔇 Mute your microphone when not speaking to reduce background noise.
        </Typography>
        <Typography variant="body2" paragraph sx={{ letterSpacing: 2 }}>
          🕒 Join on time to avoid missing key discussions.
        </Typography>
        <Typography variant="body2" paragraph sx={{ letterSpacing: 2 }}>
          🛠️ Reach out to support in advance if you face technical issues.
        </Typography>

        <Divider sx={{ my: 4, borderColor: '#333' }} />

        {/* Main Guidelines */}
        <Typography variant="h5" gutterBottom sx={{ letterSpacing: 3, textAlign: 'center' }}>
          Code of Conduct
        </Typography>

        {[
          {
            title: '1. Communicate Respectfully',
            content:
              'Maintain a courteous and respectful tone during all interactions. Disrespectful language, discriminatory remarks, or personal attacks are not tolerated.',
          },
          {
            title: '2. Zero Tolerance for Harassment',
            content:
              'Any form of bullying, intimidation, or harassment is strictly forbidden. Report incidents immediately to a moderator or platform support.',
          },
          {
            title: '3. No Inappropriate  Content',
            content:
              'Sharing or discussing explicit content is strictly prohibited. All participants are expected to maintain professional conduct at all times.',
          },
          {
            title: '4. Avoid Illegal Topics',
            content:
              'Discussions involving illegal activities (e.g., hacking, piracy, fraud) are not allowed. Violation will result in immediate removal and possible suspension.',
          },
          {
            title: '5. Respect Privacy & Confidentiality',
            content:
              'Do not record or redistribute session content without explicit permission. Protect sensitive or confidential information shared during the session.',
          },
          {
            title: '6. Use Audio/Video Responsibly',
            content:
              'Keep your mic muted when not speaking. Ensure your video feed and background are appropriate and non-distracting.',
          },
          {
            title: '7. No Spamming or Trolling',
            content:
              'Use the chat for relevant, constructive input. Spamming, trolling, or off-topic messages disrupt the session and are not acceptable.',
          },
          {
            title: '8. Report Violations Promptly',
            content:
              'If you notice any violations of these guidelines, report them immediately to ensure a safe and respectful environment.',
          },
          {
            title: '9. Consequences of Misconduct',
            content:
              'Violations of any of these terms will lead to serious consequences, including immediate removal from the session, account suspension, or a permanent ban from the platform.',
          },
          {
            title: '10. Contribute to a Positive Atmosphere',
            content:
              'We value collaboration, respect, and constructive dialogue. Let’s work together to create a productive space for everyone.',
          },
          {
            title: '11. Protect Your Personal Information',
            content:
              'Never share your contact information, banking details, or any other sensitive personal information during the session.',
          },
          {
            title: '12. Camera Usage Is Optional',
            content:
              'You are free to turn your camera or video on or off at your own comfort. Your presence and participation matter most.',
          }
        ].map((item, index) => (
          <Box key={index} sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: '#bb86fc', textAlign: 'center', letterSpacing: 3 }}
            >
              {item.title}
            </Typography>
            <Typography variant="body1" sx={{ letterSpacing: 2 }}>
              {item.content}
            </Typography>
          </Box>
        ))}

        {/* Redirection Disclaimer */}
        <Typography
          variant="body2"
          sx={{
            mt: 6,
            fontStyle: 'italic',
            textAlign: 'center',
            color: '#aaaaaa',
            letterSpacing: 1,
          }}
        >
          By clicking <strong>"Confirm and Join"</strong>, you will be redirected to an external platform.
          By proceeding, you agree to that platform’s Terms of Service and Privacy Policy.
        </Typography>

        <Divider sx={{ mt: 4, borderColor: '#333' }} />
      </Container>

      {/* Scroll to top button */}
      <IconButton
        onClick={scrollToTop}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          backgroundColor: '#333',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#555',
          },
        }}
        aria-label="Scroll to top"
      >
        <ArrowUpwardIcon />
      </IconButton>

      {/* Optional Footer */}
      {/* <Footer2 /> */}
    </Box>
  );
}

export default MeetingGuidelines;
